import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { ruoloLabel } from '../lib/ruoli';
import { useAuth } from '../context/AuthContext';
import { AlertCircle, CheckCircle2, Lock } from 'lucide-react';

export default function Register() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();

  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ nome:'', cognome:'', password:'', confirm:'' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError('Link non valido.'); return; }
    api.get(`/admin/inviti/verifica/${token}`)
      .then(r => setInvite(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Link non valido o scaduto.'));
  }, [token]);

  const [fieldErr, setFieldErr] = useState({});
  const submit = async e => {
    e.preventDefault();
    const fe = {};
    if (!form.nome.trim()) fe.nome = 'Inserisci il nome';
    if (!form.cognome.trim()) fe.cognome = 'Inserisci il cognome';
    if (form.password.length < 8) fe.password = 'Almeno 8 caratteri';
    if (form.password !== form.confirm) fe.confirm = 'Le password non corrispondono';
    setFieldErr(fe);
    if (Object.keys(fe).length) { setError(''); return; }
    setLoading(true); setError('');
    try {
      await api.post('/admin/inviti/registra', {
        token, nome: form.nome, cognome: form.cognome, password: form.password,
      });
      // Auto-login
      await login(invite.email, form.password);
      navigate('/');
    } catch(e) {
      setError(e.response?.data?.detail || 'Errore durante la registrazione.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight:'100vh', background:'var(--bg)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:24,
      position:'relative', overflow:'hidden',
    }}>
      <div style={{ position:'fixed',inset:0,pointerEvents:'none' }}>
        <div style={{ position:'absolute',width:400,height:400,borderRadius:'50%',background:'var(--cyan)',filter:'blur(130px)',opacity:0.07,top:'-5%',left:'-5%' }}/>
        <div style={{ position:'absolute',width:350,height:350,borderRadius:'50%',background:'var(--purple)',filter:'blur(130px)',opacity:0.07,bottom:'-5%',right:'-5%' }}/>
      </div>

      <div style={{
        position:'relative',zIndex:1,
        background:'rgba(14,14,26,0.9)', backdropFilter:'blur(24px)',
        border:'1px solid var(--border)', borderRadius:20,
        padding:'36px 34px', width:'100%', maxWidth:420,
        boxShadow:'var(--shadow-lg)',
      }}>
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{
            width:44,height:44,borderRadius:12,
            background:'linear-gradient(135deg,var(--cyan),var(--purple))',
            display:'flex',alignItems:'center',justifyContent:'center',
            margin:'0 auto 14px',
          }}>
            <span style={{ fontWeight:800,fontSize:20,color:'var(--on-accent)' }}>E</span>
          </div>
          <h1 style={{ fontSize:20,fontWeight:700,color:'var(--text)',marginBottom:4 }}>Completa la registrazione</h1>
          <p style={{ fontSize:12.5,color:'var(--text-muted)' }}>EdenCRM — Crea il tuo account</p>
        </div>

        {error && (
          <div style={{ display:'flex',alignItems:'center',gap:8,background:'var(--red-dim)',border:'1px solid rgba(244,63,94,0.25)',borderRadius:8,padding:'10px 12px',marginBottom:16 }}>
            <AlertCircle size={14} color="var(--red)" style={{ flexShrink:0 }}/>
            <span style={{ fontSize:12.5,color:'var(--red)' }}>{error}</span>
          </div>
        )}

        {!invite && !error && (
          <p style={{ textAlign:'center',color:'var(--text-muted)',fontSize:13 }}>Verifica del link in corso...</p>
        )}

        {invite && (
          <form onSubmit={submit} style={{ display:'flex',flexDirection:'column',gap:14 }}>
            {/* Email locked */}
            <div>
              <label style={{ fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.04em',display:'block',marginBottom:5 }}>
                Email
              </label>
              <div style={{
                display:'flex',alignItems:'center',gap:10,
                background:'rgba(255,255,255,0.03)',border:'1px solid var(--border)',
                borderRadius:8,padding:'9px 12px',
              }}>
                <span style={{ flex:1,fontSize:13.5,color:'var(--text)' }}>{invite.email}</span>
                <Lock size={13} color="var(--text-dim)" title="Email non modificabile"/>
              </div>
              <p style={{ fontSize:11,color:'var(--text-dim)',marginTop:4 }}>Email assegnata dall'amministratore — non modificabile.</p>
            </div>

            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10 }}>
              <div>
                <label style={{ fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.04em',display:'block',marginBottom:5 }}>Nome *</label>
                <input className="eden-input" value={form.nome} aria-invalid={!!fieldErr.nome} style={fieldErr.nome?{borderColor:'var(--red)'}:undefined}
                  onChange={e => { setForm(f=>({...f,nome:e.target.value})); setFieldErr(fe=>({...fe,nome:undefined})); }} required placeholder="Mario"/>
                {fieldErr.nome && <div style={{ fontSize:11,color:'var(--red)',marginTop:4 }}>{fieldErr.nome}</div>}
              </div>
              <div>
                <label style={{ fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.04em',display:'block',marginBottom:5 }}>Cognome *</label>
                <input className="eden-input" value={form.cognome} aria-invalid={!!fieldErr.cognome} style={fieldErr.cognome?{borderColor:'var(--red)'}:undefined}
                  onChange={e => { setForm(f=>({...f,cognome:e.target.value})); setFieldErr(fe=>({...fe,cognome:undefined})); }} required placeholder="Rossi"/>
                {fieldErr.cognome && <div style={{ fontSize:11,color:'var(--red)',marginTop:4 }}>{fieldErr.cognome}</div>}
              </div>
            </div>

            <div>
              <label style={{ fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.04em',display:'block',marginBottom:5 }}>Password *</label>
              <input type="password" className="eden-input" value={form.password} aria-invalid={!!fieldErr.password} style={fieldErr.password?{borderColor:'var(--red)'}:undefined}
                onChange={e => { setForm(f=>({...f,password:e.target.value})); setFieldErr(fe=>({...fe,password:undefined})); }} required placeholder="Min. 8 caratteri" autoComplete="new-password"/>
              {fieldErr.password && <div style={{ fontSize:11,color:'var(--red)',marginTop:4 }}>{fieldErr.password}</div>}
            </div>
            <div>
              <label style={{ fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.04em',display:'block',marginBottom:5 }}>Conferma password *</label>
              <input type="password" className="eden-input" value={form.confirm} aria-invalid={!!fieldErr.confirm} style={fieldErr.confirm?{borderColor:'var(--red)'}:undefined}
                onChange={e => { setForm(f=>({...f,confirm:e.target.value})); setFieldErr(fe=>({...fe,confirm:undefined})); }} required placeholder="Ripeti la password" autoComplete="new-password"/>
              {fieldErr.confirm && <div style={{ fontSize:11,color:'var(--red)',marginTop:4 }}>{fieldErr.confirm}</div>}
            </div>

            <div style={{ background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 12px',fontSize:12,color:'var(--text-muted)' }}>
              Ruolo assegnato: <span style={{ color: RUOLO_COLOR[invite.ruolo] || 'var(--cyan)', fontWeight:600 }}>{ruoloLabel(invite.ruolo)}</span>
            </div>

            <button type="submit" disabled={loading} className="eden-btn-primary" style={{ justifyContent:'center',padding:'11px',fontSize:14 }}>
              {loading ? 'Registrazione...' : 'Crea account e accedi'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const RUOLO_COLOR = { admin:'var(--cyan)', operatore:'var(--green)', agente:'var(--purple)', social_manager:'#ec4899', videomaker:'#f97316' };
